import { useEffect, useState, useRef } from "react";
import { useHomeownerAuth } from "./useHomeownerAuth";
import HomeownerGate from "./HomeownerGate";
import HomeownerWarrantyCard from "./HomeownerWarrantyCard";
import { supabaseAnon as supabase } from "../../lib/supabase";
import { useTabParam } from "../../lib/useTabParam";
import ReferralsTab from "./ReferralsTab";
import PaymentScheduleCard from "../shared/PaymentScheduleCard";
import { calcScope } from "../../lib/scopeCalc";
import { tradeMoneyOf } from "../../lib/agreementPayload";
import { lineMeta, readPicks } from "../../lib/scopeLines";
import { heicToJpeg } from "../../lib/heicToJpeg";
import { authTriple, hoPortalNotify, portalNotifyMessage } from "../../lib/homeownerRpc";
import BankVerificationNotice, { useBankVerifications, verificationTotal } from "./BankVerificationNotice";
import { homeownerColorPrompt, matchDripEdgeColor } from "../../lib/colorOptions";

// Slot derivation: anything from `schedule_build` onward (Chad approved the
// build) is treated as post-build. Everything else is pre-build = HO is
// paying ACV + out-of-pocket before construction starts.
// Mirror of create-payment-link's POST_BUILD_STAGES.
const POST_BUILD_STAGES = new Set([
  "schedule_build", "build_scheduled", "roof_complete",
  "trades_in_progress", "trades_complete",
  "commission_ready", "commission_approved", "commission_paid",
  "register_oc_warranty",
  "ready_for_supps", "ready_for_updated_invoice",
  "supps_waiting_on_claims", "supps_waiting_on_final_invoice",
  "collecting", "delinquent", "collect_payment",
  "closed_won",
]);

/* ═══════════════════════════════════════════════════════════════
   CONSTANTS
   ═══════════════════════════════════════════════════════════════ */

const DUCK_IMG = "/img/duck2.png";

/* ─── Step tracker config ─── */
const STEPS = [
  { key: "inspect", label: "Inspection" },
  { key: "review", label: "Review" },
  { key: "claim", label: "Claims" },
  { key: "approved", label: "Approved" },
  { key: "schedule", label: "Scheduling" },
  { key: "build", label: "Build" },
  { key: "trades", label: "Finishing" },
  { key: "supplements", label: "Insurance" },
  { key: "payment", label: "Payment" },
  { key: "warranty", label: "Warranty" },
  { key: "complete", label: "Complete" },
];

/* ─── stage → step index mapping ─── */
const STAGE_STEP: Record<string, { label: string; step: number; detail?: string }> = {
  // Drone
  pending_asm_review:     { label: "Appointment Under Review", step: 0 },
  appointment_received:   { label: "Appointment Scheduled", step: 0 },
  drone_scheduled:        { label: "Drone Inspection Scheduled", step: 0 },
  drone_complete:         { label: "Drone Inspection Complete", step: 0 },
  report_in_progress:     { label: "Report Being Processed", step: 1 },
  qc_review:              { label: "Report Under Review", step: 1 },
  report_approved:        { label: "Report Approved", step: 1 },
  // Leads
  initial_contact:        { label: "Your Project is Active", step: 1 },
  presentation_scheduled: { label: "Meeting Scheduled", step: 1 },
  follow_up:              { label: "Following Up", step: 1 },
  no_thanks:              { label: "On Hold", step: 1 },
  waiting:                { label: "On Hold", step: 1 },
  // Claims
  assign_roof_specialist: { label: "Roof Specialist Assigned", step: 2 },
  pre_inspection_ready:   { label: "Pre-Inspection Scheduling", step: 2 },
  pre_inspection_complete:{ label: "Pre-Inspection Complete", step: 2 },
  adjuster_meeting_ready: { label: "Adjuster Meeting Scheduling", step: 2 },
  adjuster_meeting_scheduled: { label: "Adjuster Meeting Scheduled", step: 2 },
  waiting_on_claims:      { label: "Waiting on Insurance Decision", step: 2, detail: "Your insurance company is reviewing the claim. This typically takes 7-14 business days. We'll reach out as soon as we hear back." },
  // Reinspections
  partial_approval:       { label: "Reviewing Partial Approval", step: 2, detail: "Your insurance approved part of the claim. Our team is preparing a reinspection to get the rest of the damage covered." },
  docs_and_contingency:   { label: "Preparing Reinspection", step: 2 },
  reinspection_ready:     { label: "Reinspection Ready", step: 2 },
  reinspection_filed:     { label: "Reinspection Filed", step: 2, detail: "We've filed a reinspection request with your insurance company. Our team is working with them to get your claim approved." },
  // Builds
  install_meeting:        { label: "Insurance Approved!", step: 3, detail: "Great news! Your insurance has approved the claim. We'll be scheduling an install meeting to go over everything." },
  build_submitted:        { label: "Build Under Review", step: 4 },
  schedule_build:         { label: "Scheduling Your Build", step: 4 },
  build_scheduled:        { label: "Build Scheduled!", step: 4, detail: "Your new roof is on the calendar! We'll send you details and weather updates as the date approaches." },
  roof_complete:          { label: "Roof Complete!", step: 5, detail: "Your new roof is installed! Our team is now handling any remaining finishing work." },
  trades_in_progress:     { label: "Finishing Work In Progress", step: 6, detail: "We're completing the remaining trade work (gutters, soffit, etc.) to finish your project." },
  // Closing — detailed post-build stages
  commission_ready:       { label: "Final Processing", step: 7 },
  commission_approved:    { label: "Final Processing", step: 7 },
  commission_paid:        { label: "Final Processing", step: 7 },
  ready_for_supps:        { label: "Supplement Review", step: 7, detail: "We're working with your insurance company to recover additional funds for work that was required beyond the original scope. This is normal and at no additional cost to you." },
  supps_waiting_on_claims:{ label: "Waiting on Insurance", step: 7, detail: "Your supplement has been submitted to your insurance company. Insurance supplement reviews typically take 4-6 weeks. We'll keep you posted on the status." },
  // Oct 16, 2026: the four finance stages added May–Oct had no entry here, so
  // the portal showed the raw stage name (a homeowner could read "delinquent")
  // and the tracker fell back to step 3. Never show the word delinquent.
  ready_for_updated_invoice: { label: "Waiting on Insurance", step: 7, detail: "Your completion package and supplement request are with your insurance company. We'll send your final invoice as soon as they respond." },
  supps_waiting_on_final_invoice: { label: "Waiting on Insurance", step: 7, detail: "Your supplement has been submitted to your insurance company. Insurance supplement reviews typically take 2-4 weeks. We'll send your final invoice as soon as they respond." },
  collecting:             { label: "Final Payment", step: 8, detail: "Your project is nearly complete! Any remaining balance will be reflected below." },
  delinquent:             { label: "Final Payment", step: 8, detail: "Your project is nearly complete! Any remaining balance will be reflected below." },
  register_oc_warranty:   { label: "Registering Your Warranty", step: 9, detail: "Your account is settled. We're registering your Owens Corning warranty now — the certificate will show up under Documents and in your email." },
  closed_won:             { label: "Project Complete!", step: 10, detail: "Your project is complete and your warranty is active. Thank you for choosing Bardin Roofing!" },
};

/* ─── Supplement status labels ─── */
const SUPP_STATUS: Record<string, { label: string; desc: string }> = {
  needed:    { label: "Identified", desc: "Our team has identified additional work covered by your policy. We'll be submitting this to your insurance." },
  submitted: { label: "Submitted to Insurance", desc: "The supplement has been sent to your insurance company for review. Typical review time is 4-6 weeks." },
  approved:  { label: "Approved!", desc: "Your insurance has approved the supplement. Any approved funds will be applied to your account." },
  denied:    { label: "Under Review", desc: "Insurance has responded. Our team is reviewing the response and may follow up." },
  paid:      { label: "Funds Received", desc: "Supplement funds have been received and applied to your account." },
};

function fmtUSD(n: number | null | undefined) {
  if (n == null) return "$0.00";
  return "$" + n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
// Date-only columns (build_scheduled_date, build_completed_date, …) arrive as
// "YYYY-MM-DD". `new Date("YYYY-MM-DD")` is UTC midnight, which Georgia
// renders as the previous evening — so the portal showed every build date one
// day early (Oct 16, 2026 fix). Parse date-only strings at local noon.
function parseDateSafe(d: string): Date {
  return /^\d{4}-\d{2}-\d{2}$/.test(d) ? new Date(`${d}T12:00:00`) : new Date(d);
}
function fmtDate(d: string | null | undefined) {
  if (!d) return null;
  return parseDateSafe(d).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" });
}
function fmtShortDate(d: string | null | undefined) {
  if (!d) return null;
  return parseDateSafe(d).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

/* ═══════════════════════════════════════════════════════════════
   STEP TRACKER with duck mascot
   ═══════════════════════════════════════════════════════════════ */

function StepTracker({ currentStep }: { currentStep: number }) {
  return (
    <div className="relative">
      {/* Duck mascot at current step */}
      <div className="flex justify-center mb-2">
        <div
          className="transition-all duration-500 ease-in-out"
          style={{ marginLeft: `${(currentStep / (STEPS.length - 1)) * 80 - 40}%` }}
        >
          <img src={DUCK_IMG} alt="" className="w-10 h-10 object-contain" />
        </div>
      </div>
      {/* Progress bar */}
      <div className="relative h-2 bg-gray-200 rounded-full mb-2">
        <div
          className="absolute h-2 bg-gradient-to-r from-bardin-teal to-bardin-orange rounded-full transition-all duration-500"
          style={{ width: `${(currentStep / (STEPS.length - 1)) * 100}%` }}
        />
      </div>
      {/* Step labels — show subset to avoid crowding on mobile */}
      <div className="flex justify-between text-[9px] text-gray-400 px-0">
        {STEPS.map((s, i) => {
          const done = i < currentStep;
          const active = i === currentStep;
          // On mobile show every other label, or always show active/done endpoints
          const show = active || i === 0 || i === STEPS.length - 1 || i % 2 === 0;
          return (
            <span
              key={s.key}
              className={`text-center leading-tight ${
                active ? "font-bold text-bardin-orange" : done ? "text-bardin-teal" : ""
              } ${show ? "" : "hidden sm:inline"}`}
              style={{ width: `${100 / STEPS.length}%` }}
            >
              {s.label}
            </span>
          );
        })}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   SECTION COMPONENTS
   ═══════════════════════════════════════════════════════════════ */

function Section({ title, children, icon }: { title: string; children: React.ReactNode; icon?: string }) {
  return (
    <div className="bg-white rounded-xl p-5 shadow-sm">
      <h2 className="font-semibold text-bardin-ink mb-3 flex items-center gap-2">
        {icon && <span className="text-lg">{icon}</span>}
        {title}
      </h2>
      {children}
    </div>
  );
}

function InfoRow({ label, value, highlight }: { label: string; value: string | null | undefined; highlight?: boolean }) {
  if (!value) return null;
  return (
    <div className="flex justify-between text-sm py-1">
      <span className="text-gray-500">{label}</span>
      <span className={`font-medium ${highlight ? "text-bardin-orange" : ""}`}>{value}</span>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════════════════════════════ */

/** Certificate of Completion download (Jul 14, 2026 — net on screen, gross on
 *  paper). The only documents a homeowner can save from the portal are
 *  carrier-safe: this certificate shows completion facts + the gross contract
 *  value, never balances or payment history. Carriers commonly request it to
 *  release recoverable depreciation. Self-contained so it owns its own state. */
function CocDownloadCard({ deal }: { deal: Record<string, unknown> }) {
  const [generating, setGenerating] = useState(false);
  if (!deal.build_completed_date) return null;
  const handleDownload = async () => {
    if (generating) return;
    setGenerating(true);
    try {
      const { generateCertificateOfCompletionPDF } = await import("../../lib/certificateOfCompletionPDF");
      const bytes = await generateCertificateOfCompletionPDF(deal as Parameters<typeof generateCertificateOfCompletionPDF>[0]);
      const blob = new Blob([bytes as BlobPart], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "Bardin-Roofing-Certificate-of-Completion.pdf";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch {
      window.alert("Couldn't generate the certificate. Please text us at (470) 823-8816 and we'll send you a copy.");
    } finally {
      setGenerating(false);
    }
  };
  return (
    <Section title="Your Documents" icon="📄">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="text-sm font-semibold text-bardin-ink">Certificate of Completion</div>
          <p className="text-xs text-gray-500 mt-0.5">
            Proof your roof is complete — this is the document your insurance company may ask for to release your depreciation.
          </p>
        </div>
        <button
          onClick={handleDownload}
          disabled={generating}
          className="text-sm font-semibold px-4 py-2 rounded-lg bg-bardin-teal text-white hover:opacity-90 transition disabled:opacity-50 flex-shrink-0"
        >
          {generating ? "Preparing…" : "⬇ Download PDF"}
        </button>
      </div>
    </Section>
  );
}

export default function StatusPortal() {
  const auth = useHomeownerAuth();
  const { deal } = auth;

  const [msg, setMsg] = useState("");
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [msgError, setMsgError] = useState<string | null>(null);

  // File upload state
  const fileInputRef = useRef<HTMLInputElement>(null);
  interface UploadedFile { id: string; name: string; status: "uploading" | "done" | "error" }
  const [uploads, setUploads] = useState<UploadedFile[]>([]);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [dragActive, setDragActive] = useState(false);

  // Dep-release completion photos (for un-performed scope items)
  const depReleaseInputRef = useRef<HTMLInputElement>(null);
  const [depReleaseUploading, setDepReleaseUploading] = useState(false);
  const [depReleaseSubmitted, setDepReleaseSubmitted] = useState(false);
  const [depReleaseError, setDepReleaseError] = useState<string | null>(null);

  // Payment link state
  const [creatingLink, setCreatingLink] = useState<"ach" | "card" | null>(null);
  const [linkError, setLinkError] = useState<string | null>(null);
  const [customAmount, setCustomAmount] = useState<string>("");

  // Active tab
  // Tab state syncs with URL ?tab=... so the post-sign redirect (and any
  // future deep-link from email/SMS) can land directly on Documents or
  // Payment instead of always opening to Status.
  const [tab, setTab] = useTabParam<"status" | "documents" | "payment" | "referrals">(
    "tab",
    "status",
    ["status", "documents", "payment", "referrals"] as const,
  );

  // Insurance paper trail (initial estimate / Bardin supplement invoice /
  // revised estimate) — Jul 15, 2026. These live in the private deal-documents
  // bucket, so the ho-portal-documents edge function validates the portal auth
  // triple and returns 1-hour signed URLs. Re-fetched every time the Documents
  // tab opens so the links are always fresh.
  const [hoDocs, setHoDocs] = useState<
    { file_type: string; file_name: string; created_at: string; url: string }[]
  >([]);
  useEffect(() => {
    if (!deal || tab !== "documents") return;
    let cancelled = false;
    const t = authTriple(deal);
    supabase.functions
      .invoke("ho-portal-documents", {
        body: { deal_id: t.dealId, last_name: t.lastName, street_num: t.streetNum },
      })
      .then(({ data }) => {
        if (!cancelled && data?.ok) setHoDocs(data.documents ?? []);
      })
      .catch(() => { /* best-effort — the rest of the tab still renders */ });
    return () => { cancelled = true; };
  }, [tab, deal]);

  // Bank payments typed in by hand that are still waiting on the homeowner
  // to confirm Stripe's test deposit (Oct 20, 2026).
  const bankVerifications = useBankVerifications(deal);

  if (!deal) {
    return <HomeownerGate {...auth} title="Project Status">{null}</HomeownerGate>;
  }

  const stageInfo = STAGE_STEP[deal.stage] || { label: deal.stage.replace(/_/g, " "), step: 3 };
  const preReceived = Number(deal.pre_build_amount_received ?? 0);
  const postReceived = Number(deal.post_build_amount_received ?? 0);
  // In-flight = homeowner has paid but the money hasn't bank-cleared yet
  // (check in the iink pipeline, Stripe payment awaiting clearing). From the
  // homeowner's perspective this IS paid — Jun 10, 2026 fix: without it,
  // customers saw $0 paid and full balance due for days/weeks after paying.
  const preInFlight = Number(deal.pre_build_amount_in_flight ?? 0);
  const postInFlight = Number(deal.post_build_amount_in_flight ?? 0);
  const inFlight = preInFlight + postInFlight;
  const amountPaid = preReceived + postReceived + inFlight;
  // Oct 20, 2026: part of in-flight may be a bank payment that can't move
  // until the homeowner verifies their account. It still counts as paid (so
  // nobody is asked to pay twice), but it is not "processing, no action".
  const awaitingVerify = Math.min(inFlight, verificationTotal(bankVerifications));
  const processingOnly = Math.max(0, inFlight - awaitingVerify);
  // Jul 14, 2026: the old "Total Contract" row fell back to acv_amount — a
  // mislabel that put a third, unrelated number on the Payment Summary. The
  // summary now shows ONE invoice figure: the same source Balance Due derives
  // from (invoicedForPostBuild below), so Invoice Total − Amount Paid =
  // Balance Due reconciles on screen.

  // Live "what does the homeowner owe RIGHT NOW" — May 8, 2026 fix.
  // Pre-build deals (no final invoice yet) need a balance signal too. We use
  // the same scope calc that drives the install agreement PDF + Payment
  // Schedule card so the number on the Pay button matches the number above.
  const scope = calcScope(deal as unknown as Record<string, unknown>);
  // Oct 29, 2026: trades billed to the homeowner from the trade sheet — due at
  // completion, never in the first payment (they are in the invoice total).
  const tradesDueAtCompletion = tradeMoneyOf(scope).total;
  // Oct 30, 2026 (scope gate): the carrier lines Bardin won't do, as the RM
  // ticked them, for the depreciation-photos card (instead of line numbers).
  const notDonePicks = deal.is_retail === true ? [] : readPicks(deal.items_not_performing_detail);
  const slot: "pre_build" | "post_build" = POST_BUILD_STAGES.has(deal.stage)
    ? "post_build"
    : "pre_build";
  // For pre-build: outstanding = first payment to Bardin − what's already collected pre-build.
  // For post-build: prefer Shannon's stamped invoice (final/updated/initial),
  // fall back to scope-derived initialInvoice; subtract everything received.
  const invoicedForPostBuild = Number(
    deal.final_invoice_amount ??
    deal.updated_invoice_amount ??
    deal.initial_invoice_amount ??
    scope.initialInvoice ??
    0,
  );
  // A residual Shannon wrote off (balance_written_off, May 21, 2026) is not
  // owed — recompute_deal_payment_summary already subtracts it server-side,
  // but this on-screen figure and create-payment-link compute their own.
  const writtenOff = Math.max(0, Number(deal.balance_written_off ?? 0));
  const outstandingNow = slot === "pre_build"
    ? Math.max(0, scope.firstPaymentToBardin - preReceived - preInFlight)
    : Math.max(0, invoicedForPostBuild - amountPaid - writtenOff);
  // The legacy `balance` name still flows through downstream JSX. Keep the
  // variable but redefine it to mean "what's owed right now" instead of just
  // post_build_balance_remaining.
  const balance = outstandingNow;

  // Has finance actually set a finalized invoice? post_build_balance_remaining
  // defaults to 0 in the DB before any scope/invoice exists, so we can't trust
  // "balance == 0" as a paid-in-full signal on its own. Need either:
  //   (a) a real finalized/updated invoice that the payments have covered, or
  //   (b) the deal has reached a terminal-payment stage (warranty registration
  //       or closed_won), which only happens after Shannon books the dep settlement.
  const finalizedInvoice = Math.max(
    Number(deal.updated_invoice_amount ?? 0),
    Number(deal.final_invoice_amount ?? 0),
  );
  const invoiceFinalized = finalizedInvoice > 0;
  const terminalPaymentStage =
    deal.stage === "register_oc_warranty" || deal.stage === "closed_won";
  const isFullyPaid =
    amountPaid > 0 &&
    (
      (invoiceFinalized && amountPaid + writtenOff + 0.01 >= finalizedInvoice) ||
      (terminalPaymentStage && balance <= 0)
    );
  // Money's in but more is still owed (depreciation settlement, supplements,
  // final invoice). Brian's case: $16,836.87 paid against a not-yet-finalized
  // dep portion that the carrier still has to release.
  const isFirstPaymentReceived = !isFullyPaid && amountPaid > 0;
  const firstName = deal.homeowner_name?.split(" ")[0]?.replace("TEST - ", "") || "there";

  /* ─── Handlers ─── */

  // The message lands on the deal as the homeowner's and reaches whoever owns
  // the conversation (ho-portal-notify). On failure the words stay in the box.
  const handleSendMessage = async () => {
    const text = msg.trim();
    if (!text || sending) return;
    setSending(true);
    setMsgError(null);
    const res = await hoPortalNotify(authTriple(deal), "message", { body: text });
    setSending(false);
    if (!res.ok) {
      setMsgError(portalNotifyMessage(res.code));
      return;
    }
    setSent(true);
    setMsg("");
    setTimeout(() => setSent(false), 5000);
  };

  // Files go to the homeowner-uploads drop folder; one call then files them
  // on the deal's Documents tab and alerts the team.
  const handleFiles = async (fileList: FileList | null) => {
    if (!fileList || fileList.length === 0) return;
    const picked = Array.from(fileList);
    const stamp = Date.now();
    const entries: UploadedFile[] = picked.map((f, i) => ({ id: `${stamp}-${i}`, name: f.name, status: "uploading" }));
    setUploads((prev) => [...prev, ...entries]);
    setUploadError(null);
    const setStatus = (id: string, status: UploadedFile["status"]) =>
      setUploads((prev) => prev.map((u) => (u.id === id ? { ...u, status } : u)));

    const stored: Array<{ id: string; path: string }> = [];
    await Promise.all(picked.map(async (p, i) => {
      const id = entries[i].id;
      try {
        // Convert iPhone HEIC/HEIF to JPEG so homeowner-uploaded photos render in
        // every browser the office team uses. No-op for PDFs/JPEGs/PNGs.
        const file = await heicToJpeg(p);
        const path = `${deal.id}/${stamp + i}-${file.name}`;
        const { error } = await supabase.storage
          .from("homeowner-uploads")
          .upload(path, file, { upsert: false });
        if (error) setStatus(id, "error");
        else stored.push({ id, path });
      } catch {
        setStatus(id, "error");
      }
    }));
    if (stored.length === 0) {
      setUploadError(portalNotifyMessage("failed"));
      return;
    }

    const res = await hoPortalNotify(authTriple(deal), "upload", {
      source: "status_page",
      files: stored.map((s) => ({ path: s.path })),
    });
    const results = (res.data?.files ?? []) as Array<{ path: string; ok: boolean }>;
    for (const s of stored) {
      const r = results.find((x) => x.path === s.path);
      setStatus(s.id, r?.ok ? "done" : "error");
    }
    if (!res.ok) {
      setUploadError(portalNotifyMessage(res.code));
    } else if (stored.length < picked.length || results.some((r) => !r.ok)) {
      setUploadError("Some files didn't come through (marked ❌). Please try those again, or text them to (470) 823-8816.");
    }
  };

  // Upload completion photos for un-performed line items (unlocks dep release).
  // ho-portal-notify files them on the deal, marks them received, and alerts
  // the team that they're ready for the carrier.
  const uploadDepReleasePhotos = async (fileList: FileList | null) => {
    if (!fileList || fileList.length === 0) return;
    setDepReleaseUploading(true);
    setDepReleaseError(null);
    try {
      const picked = Array.from(fileList);
      const stamp = Date.now();
      const paths: string[] = [];
      for (let i = 0; i < picked.length; i++) {
        // Convert iPhone HEIC/HEIF to JPEG so dep-release completion photos
        // render in every browser. No-op for non-HEIC files.
        const file = await heicToJpeg(picked[i]);
        const path = `${deal.id}/dep-release/${stamp + i}-${file.name}`;
        const { error: upErr } = await supabase.storage
          .from("homeowner-uploads")
          .upload(path, file, { upsert: false });
        if (upErr) throw new Error("One of the photos didn't upload. Please try again, or text them to (470) 823-8816.");
        paths.push(path);
      }
      const res = await hoPortalNotify(authTriple(deal), "dep_release", {
        files: paths.map((path) => ({ path })),
      });
      if (!res.ok) throw new Error(portalNotifyMessage(res.code));
      setDepReleaseSubmitted(true);
    } catch (e) {
      setDepReleaseError((e as Error).message);
    } finally {
      setDepReleaseUploading(false);
    }
  };

  const handlePayNow = async (method: "ach" | "card") => {
    // Determine pay amount: custom (if provided) else full outstanding balance.
    // No `balance <= 0` short-circuit anymore — when balance is unknown the HO
    // can still type a custom amount and pay early (Greg's May 8, 2026 rule:
    // keep the Pay button live until the deal is paid in full).
    const customNum = customAmount ? parseFloat(customAmount) : 0;
    const useCustom = customAmount !== "" && customNum > 0;
    const payAmount = useCustom ? customNum : balance;
    if (!payAmount || payAmount <= 0 || isNaN(payAmount)) {
      setLinkError("Please enter a valid payment amount.");
      return;
    }
    if (balance > 0 && payAmount > balance) {
      setLinkError("Payment amount cannot exceed balance due.");
      return;
    }

    // Re-derive the URL token (HO came in via one).
    const sp = new URLSearchParams(window.location.search);
    const token = sp.get("token") || "";
    if (!token) {
      setLinkError("Session expired. Please reopen the link from your text message.");
      return;
    }

    setCreatingLink(method);
    setLinkError(null);
    try {
      const { data, error } = await supabase.functions.invoke("create-payment-link", {
        body: {
          token,
          payment_method: method,
          amount_mode: useCustom ? "custom" : "full",
          amount_cents: useCustom ? Math.round(customNum * 100) : undefined,
        },
      });
      if (error) throw error;
      if (data?.url) window.location.href = data.url;
      else setLinkError("Unable to create payment link. Please call us at (470) 823-8816.");
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Something went wrong.";
      setLinkError(`${msg} If the issue persists, call us at (470) 823-8816.`);
    } finally {
      setCreatingLink(null);
    }
  };

  const payAmount = customAmount ? parseFloat(customAmount) || 0 : balance;
  // Tiered fee: payments ≤ $1,500 are free on both methods. Above that, card adds 3%.
  // Keep the $1,500 threshold in sync with create-payment-link's FEE_FREE_CEILING_CENTS.
  const FEE_FREE_CEILING = 1500;
  const cardSurchargeApplies = payAmount > FEE_FREE_CEILING;
  const cardSurcharge = cardSurchargeApplies ? payAmount * 0.03 : 0;
  const cardTotal = payAmount + cardSurcharge;
  const isPartialPayment = customAmount !== "" && payAmount < balance && payAmount > 0;

  /* ─── Document list builder ─── */
  // Colors still to choose or confirm (Oct 22, 2026): once the agreement is
  // signed, while the Color Confirmation Addendum is still needed and
  // unsigned, until materials are ordered. The banner opens /colors with this
  // homeowner's own link, so a lost text no longer strands them.
  const colorPrompt = deal ? homeownerColorPrompt(deal) : null;
  const hoToken = new URLSearchParams(window.location.search).get("token") || "";

  const documents: { label: string; url: string | null; status: string; date?: string | null }[] = [];
  // Drone report — only visible when RM shares it
  if (deal.drone_report_shared_with_ho && deal.imging_report_link) {
    documents.push({ label: "Drone Inspection Report", url: deal.imging_report_link, status: "available" });
  }
  // Insurance paper trail from deal_documents (via ho-portal-documents):
  // initial carrier estimate → Bardin supplement invoice → carrier's revised
  // (final) estimate. Legacy scope_document_url only shows when no
  // deal_documents insurance_scope exists (avoids a duplicate row).
  const HO_DOC_LABELS: Record<string, string> = {
    insurance_scope: "Insurance Estimate — Initial",
    supplement_invoice: "Supplement Invoice — Sent to Insurance",
    finalized_insurance_paperwork: "Insurance Estimate — Revised (Final)",
    oc_warranty_certificate: "Owens Corning Warranty Certificate",
  };
  const hasScopeDoc = hoDocs.some((d) => d.file_type === "insurance_scope");
  if (deal.scope_document_url && !hasScopeDoc) {
    documents.push({ label: "Insurance Scope", url: deal.scope_document_url, status: "available" });
  }
  for (const d of hoDocs) {
    documents.push({
      label: HO_DOC_LABELS[d.file_type] ?? d.file_name,
      url: d.url,
      status: "available",
      date: d.created_at,
    });
  }
  // Install agreement — visible when shared with HO (after Chad approval).
  // Shows even when pdf_url is missing so the HO sees their signed status;
  // if signed-but-no-PDF, the URL is null and renders as a non-clickable
  // entry with "PDF being prepared" note (Apr 30 fix for the silent PDF
  // generation failures from before the storage policy was in place).
  if (deal.install_agreement_shared_with_ho || deal.install_agreement_signed) {
    documents.push({
      label: "Install Agreement",
      url: deal.install_agreement_pdf_url ?? null,
      status: deal.install_agreement_signed ? "signed" : "pending",
      date: deal.install_agreement_signed_date,
    });
  }
  if (deal.color_addendum_url) documents.push({ label: "Color Confirmation", url: deal.color_addendum_url, status: deal.color_addendum_signed ? "signed" : "pending" });
  if (deal.initial_invoice_pdf_sent) documents.push({ label: "Initial Invoice", url: deal.living_invoice_url, status: "sent" });
  if (deal.transparency_invoice_pdf_sent) documents.push({ label: "Supplement Invoice (Transparency)", url: deal.living_invoice_url, status: "sent" });
  if (deal.final_invoice_pdf_sent) documents.push({ label: "Final Invoice", url: deal.living_invoice_url, status: "sent" });

  /* ─── Tab navigation ─── */
  const tabs = [
    { key: "status" as const, label: "Status", icon: "📊" },
    { key: "documents" as const, label: "Documents", icon: "📄" },
    { key: "payment" as const, label: "Payment", icon: "💳" },
    { key: "referrals" as const, label: "Referrals", icon: "🤝" },
  ];

  return (
    <HomeownerGate {...auth} title="My Project">
      <div className="space-y-4">
        {/* ─── Header with duck ─── */}
        <div className="bg-white rounded-xl p-5 shadow-sm">
          <div className="flex items-center gap-3 mb-2">
            <img src={DUCK_IMG} alt="" className="w-12 h-12 object-contain" />
            <div>
              <h1 className="text-lg font-bold text-bardin-ink">
                Hi, {firstName}!
              </h1>
              <p className="text-sm text-gray-500">{deal.property_street}</p>
            </div>
          </div>
          {deal.owner_name && (
            <p className="text-sm text-gray-500 ml-15">
              Your Restore Manager: <span className="font-medium text-bardin-teal">{deal.owner_name}</span>
            </p>
          )}
        </div>

        {/* ─── Tab Bar ─── */}
        <div className="flex gap-1 bg-white rounded-xl p-1 shadow-sm">
          {tabs.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`flex-1 py-2 px-1 rounded-lg text-xs sm:text-sm font-medium transition ${
                tab === t.key
                  ? "bg-bardin-teal text-white shadow-sm"
                  : "text-gray-500 hover:bg-gray-50"
              }`}
            >
              {/* Icon above the label on phones: with 4 tabs, "Documents" and
                  "Referrals" wrap on a 320px screen when laid out inline. */}
              <span className="block sm:inline sm:mr-1 leading-none">{t.icon}</span>
              <span className="block sm:inline mt-0.5 sm:mt-0">{t.label}</span>
            </button>
          ))}
        </div>

        {/* ═══════════════ STATUS TAB ═══════════════ */}
        {tab === "status" && (
          <>
            {/* Install Agreement — Ready to Sign.
                BIG BANNER on first thing the HO sees after login. Gates on
                install_agreement_shared_with_ho (RM clicked Share) so the RM
                controls when this surfaces, and stays visible until the HO
                actually signs (covers status='approved' AND status='sent'). */}
            {deal.install_agreement_shared_with_ho && !deal.install_agreement_signed && (
              <div className="bg-gradient-to-br from-bardin-orange to-bardin-peach rounded-2xl p-6 shadow-lg border-2 border-bardin-orange ring-4 ring-bardin-orange/20">
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-4xl">📝</span>
                  <div>
                    <h2 className="font-bold text-white text-xl leading-tight">Your Install Agreement is Ready</h2>
                    <p className="text-sm text-white/95 mt-0.5">Please review and sign so we can schedule your build.</p>
                  </div>
                </div>
                <a
                  href={deal.signing_link || deal.agreement_signing_url || `/sign?token=${new URLSearchParams(window.location.search).get("token") || ""}`}
                  className="block w-full bg-white text-bardin-orange font-bold rounded-lg py-4 text-center hover:bg-white/95 transition text-lg shadow-sm"
                >
                  Review &amp; Sign Agreement →
                </a>
              </div>
            )}

            {/* Colors — choose (a color still open) or confirm (staff typed
                both in; only the signature is missing). Same look as the
                agreement banner above; the two never show together. */}
            {colorPrompt && (
              <div className="bg-gradient-to-br from-bardin-orange to-bardin-peach rounded-2xl p-6 shadow-lg border-2 border-bardin-orange ring-4 ring-bardin-orange/20">
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-4xl">🎨</span>
                  <div>
                    <h2 className="font-bold text-white text-xl leading-tight">
                      {colorPrompt === "confirm" ? "Confirm Your Colors" : "Choose Your Shingle and Drip Edge Colors"}
                    </h2>
                    <p className="text-sm text-white/95 mt-0.5">
                      {colorPrompt === "confirm"
                        ? `We have ${deal.shingle_color} shingles and ${matchDripEdgeColor(deal.drip_edge_color) ?? deal.drip_edge_color} drip edge on file. Please confirm them with a quick signature so we can order materials.`
                        : "We can't order materials until your colors are confirmed. It takes about a minute."}
                    </p>
                  </div>
                </div>
                <a
                  href={`/colors?token=${encodeURIComponent(hoToken)}`}
                  className="block w-full bg-white text-bardin-orange font-bold rounded-lg py-4 text-center hover:bg-white/95 transition text-lg shadow-sm"
                >
                  {colorPrompt === "confirm" ? "Confirm My Colors →" : "Choose My Colors →"}
                </a>
              </div>
            )}

            <BankVerificationNotice items={bankVerifications} />

            {/* Progress Tracker */}
            <Section title="Project Progress" icon="🏠">
              <StepTracker currentStep={stageInfo.step} />
              <div className="mt-4 bg-bardin-sky rounded-lg p-4">
                <p className="text-sm font-medium text-bardin-teal">Current Status</p>
                <p className="text-bardin-ink font-bold text-lg">{stageInfo.label}</p>
                {stageInfo.detail && (
                  <p className="text-sm text-gray-600 mt-2">{stageInfo.detail}</p>
                )}
              </div>
            </Section>

            {/* Build Day Guide (Oct 16, 2026) — the public what-to-expect page
                (public/build-day, same site, no token). Shown on every deal so
                it is there before, during and after the build. */}
            <a
              href="/build-day"
              target="_blank"
              rel="noopener noreferrer"
              className="block bg-white rounded-xl p-4 shadow-sm border border-bardin-sky hover:bg-bardin-sky/30 transition"
            >
              <div className="flex items-center gap-3">
                <span className="text-3xl">📘</span>
                <div className="flex-1 min-w-0">
                  <h2 className="font-semibold text-bardin-ink">Build Day Guide</h2>
                  <p className="text-sm text-gray-500">
                    What to expect before, during and after your new roof, and what to check around your home afterward.
                  </p>
                </div>
                <span className="text-xs font-semibold px-2 py-1 rounded bg-bardin-teal text-white shrink-0">Open →</span>
              </div>
            </a>

            {/* Un-performed Line Items — upload completion photos to unlock depreciation release.
                Insurance-only — leftover claim values on a converted retail deal don't apply. */}
            {deal.is_retail !== true
              && deal.items_not_performing_lines
              && ((deal.acv_credit ?? 0) + (deal.dep_credit ?? 0)) > 0
              && !deal.unperf_completion_uploaded_at
              && !depReleaseSubmitted && (
              <div className="bg-gradient-to-r from-amber-50 to-amber-100/50 rounded-xl p-5 shadow-sm border border-amber-200">
                <div className="flex items-start gap-3 mb-3">
                  <span className="text-3xl">📸</span>
                  <div className="flex-1">
                    <h2 className="font-bold text-bardin-ink text-lg">Your Depreciation Check is Waiting on Photos</h2>
                    <p className="text-sm text-gray-700 mt-1">
                      Insurance is holding the depreciation on items Bardin isn't doing. Upload completion photos (or photos showing the area is fine as-is) and we'll help you get that money released directly to you.
                    </p>
                  </div>
                </div>
                <div className="bg-white rounded-lg p-3 mb-3 border border-amber-200">
                  <div className="text-xs font-semibold text-amber-800 uppercase tracking-wider mb-1">Items awaiting completion</div>
                  {notDonePicks.length > 0 ? (
                    <ul className="space-y-1.5">
                      {notDonePicks.map((p, i) => {
                        const meta = lineMeta(p);
                        const held = p.dep_nonrec ? 0 : p.dep;
                        return (
                          <li key={i} className="flex items-start justify-between gap-3 text-sm text-bardin-ink">
                            <span className="min-w-0">
                              {p.description}
                              {meta && <span className="block text-xs text-gray-500">{meta}</span>}
                            </span>
                            {held > 0 && (
                              <span className="shrink-0 text-xs text-gray-600">
                                ${held.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} held
                              </span>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  ) : (
                    <div className="text-sm text-bardin-ink">{deal.items_not_performing_lines}</div>
                  )}
                  <div className="text-xs text-gray-600 mt-2">
                    Depreciation held: <span className="font-semibold text-amber-900">${(deal.dep_credit ?? 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    {" · "}
                    {/* Credits stop at the deductible (Oct 30, 2026): show what was actually credited. */}
                    ACV portion (credited toward your deductible): <span className="font-semibold text-amber-900">${Math.min(Number(deal.acv_credit ?? 0), deal.deductible == null ? Infinity : Number(deal.deductible)).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                  </div>
                </div>
                <button
                  onClick={() => depReleaseInputRef.current?.click()}
                  disabled={depReleaseUploading}
                  className="block w-full bg-amber-600 text-white font-semibold rounded-lg py-3.5 text-center hover:bg-amber-700 transition text-base shadow-sm disabled:opacity-60"
                >
                  {depReleaseUploading ? "Uploading…" : "📷 Upload Completion Photos"}
                </button>
                <input
                  ref={depReleaseInputRef}
                  type="file"
                  multiple
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => uploadDepReleasePhotos(e.target.files)}
                />
                {depReleaseError && (
                  <p className="text-xs text-red-600 mt-2">{depReleaseError}</p>
                )}
                <p className="text-xs text-gray-500 mt-2 leading-relaxed">
                  Tap the button to select photos from your phone. We'll forward them to your insurance along with the release request. Please don't contact your insurance directly about these items — let us handle it.
                </p>
              </div>
            )}

            {/* Post-submit confirmation */}
            {(deal.unperf_completion_uploaded_at || depReleaseSubmitted) && deal.items_not_performing_lines && ((deal.acv_credit ?? 0) + (deal.dep_credit ?? 0)) > 0 && (
              <div className="bg-emerald-50 rounded-xl p-4 shadow-sm border border-emerald-200">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">✓</span>
                  <div>
                    <h3 className="font-semibold text-emerald-800">Completion photos received</h3>
                    <p className="text-sm text-emerald-700">We'll forward them to your insurance and follow up on the depreciation release.</p>
                  </div>
                </div>
              </div>
            )}

            {/* Key Information */}
            <Section title="Project Details" icon="📋">
              <div className="space-y-1">
                <InfoRow label="Insurance" value={deal.insurance_company} />
                <InfoRow label="Claim #" value={deal.claim_number} />
                <InfoRow label="Shingle Color" value={deal.shingle_color} />
                <InfoRow label="Drip Edge Color" value={deal.drip_edge_color} />
                {deal.build_scheduled_date && (
                  deal.build_end_date && deal.build_end_date.slice(0, 10) > deal.build_scheduled_date.slice(0, 10)
                    ? <InfoRow label="Build Dates" value={`${fmtDate(deal.build_scheduled_date)} – ${fmtDate(deal.build_end_date)}`} highlight />
                    : <InfoRow label="Build Date" value={fmtDate(deal.build_scheduled_date)} highlight />
                )}
                {deal.build_completed_date && (
                  <InfoRow label="Build Completed" value={fmtDate(deal.build_completed_date)} />
                )}
              </div>
            </Section>

            {/* Supplement Explanation — only show when relevant */}
            {deal.supplement_needed && (
              <Section title="Supplement Status" icon="📑">
                <div className="bg-blue-50 rounded-lg p-4 mb-3">
                  <h3 className="font-semibold text-blue-800 text-sm mb-1">What is a supplement?</h3>
                  <p className="text-xs text-blue-700 leading-relaxed">
                    During your roof replacement, our team may discover additional work that wasn't visible during the initial inspection —
                    things like damaged decking, code-required upgrades, or items the insurance adjuster may have missed.
                    We submit these items to your insurance company for additional coverage. This process is normal,
                    and there is <span className="font-semibold">no additional cost to you</span> beyond your deductible.
                  </p>
                </div>
                {deal.supplement_status && SUPP_STATUS[deal.supplement_status] && (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        deal.supplement_status === "approved" || deal.supplement_status === "paid"
                          ? "bg-green-100 text-green-700"
                          : "bg-amber-100 text-amber-700"
                      }`}>
                        {SUPP_STATUS[deal.supplement_status].label}
                      </span>
                      {deal.supplement_submitted_to_insurance_date && (
                        <span className="text-xs text-gray-400">
                          Submitted {fmtShortDate(deal.supplement_submitted_to_insurance_date)}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600">
                      {SUPP_STATUS[deal.supplement_status].desc}
                    </p>
                    {deal.supplement_approved_amount != null && deal.supplement_approved_amount > 0 && (
                      <div className="flex justify-between text-sm pt-2 border-t">
                        <span className="text-gray-500">Approved Amount</span>
                        <span className="font-medium text-green-600">{fmtUSD(deal.supplement_approved_amount)}</span>
                      </div>
                    )}
                  </div>
                )}
              </Section>
            )}

            {/* Warranty info — only show when registered.
                Ship 5 (May 7, 2026): replaced inline display with HomeownerWarrantyCard
                so HOs can report issues + see prior claims. */}
            {deal.warranty_registered_date && (
              <Section title="Your Warranty" icon="🛡️">
                <HomeownerWarrantyCard deal={deal} duckImg={DUCK_IMG} />
              </Section>
            )}

            {/* Owens Corning Widgets */}
            <Section title="Explore Your Roof" icon="🎨">
              <p className="text-sm text-gray-500 mb-3">
                Visualize shingle options on your home and explore warranty coverage.
              </p>
              <div className="grid grid-cols-1 gap-2">
                <a
                  href="https://www.owenscorning.com/en-us/roofing/designeyeq"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 border border-gray-200 rounded-lg p-3 hover:bg-bardin-sky/30 transition"
                >
                  <span className="text-2xl">🏡</span>
                  <div>
                    <p className="text-sm font-medium text-bardin-ink">Design EyeQ Visualizer</p>
                    <p className="text-xs text-gray-400">See shingle colors on your home</p>
                  </div>
                </a>
                <a
                  href="https://www.owenscorning.com/en-us/roofing/shingles"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 border border-gray-200 rounded-lg p-3 hover:bg-bardin-sky/30 transition"
                >
                  <span className="text-2xl">🔲</span>
                  <div>
                    <p className="text-sm font-medium text-bardin-ink">Shingle Browser</p>
                    <p className="text-xs text-gray-400">Browse all available shingle options</p>
                  </div>
                </a>
                <a
                  href="https://www.owenscorning.com/en-us/roofing/total-protection-roofing-system"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 border border-gray-200 rounded-lg p-3 hover:bg-bardin-sky/30 transition"
                >
                  <span className="text-2xl">🛡️</span>
                  <div>
                    <p className="text-sm font-medium text-bardin-ink">Total Protection System</p>
                    <p className="text-xs text-gray-400">Learn about your roof's protection layers</p>
                  </div>
                </a>
              </div>
            </Section>

            {/* Message Us */}
            <Section title="Message Us" icon="💬">
              <p className="text-xs text-gray-400 mb-3">
                Send a message and your Restore Manager will get back to you.
              </p>
              {sent ? (
                <div className="bg-green-50 text-green-700 rounded-lg p-3 text-sm text-center">
                  Message sent! We'll get back to you soon.
                </div>
              ) : (
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={msg}
                    onChange={(e) => setMsg(e.target.value)}
                    placeholder="Type your message..."
                    className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-bardin-teal focus:border-transparent"
                    onKeyDown={(e) => e.key === "Enter" && handleSendMessage()}
                  />
                  <button
                    onClick={handleSendMessage}
                    disabled={!msg.trim() || sending}
                    className="bg-bardin-teal text-white px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50 hover:bg-bardin-teal/90 transition"
                  >
                    {sending ? "..." : "Send"}
                  </button>
                </div>
              )}
              {msgError && !sent && (
                <p className="text-xs text-red-600 mt-2">{msgError}</p>
              )}
            </Section>
          </>
        )}

        {/* ═══════════════ DOCUMENTS TAB ═══════════════ */}
        {tab === "documents" && (
          <>
            {/* Your Documents */}
            <Section title="Your Documents" icon="📄">
              {/* Build Day Guide row (Oct 16, 2026) — static page, always available. */}
              <a
                href="/build-day"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-between border border-gray-200 rounded-lg p-3 mb-2 hover:bg-bardin-sky/30 transition"
              >
                <div className="flex items-center gap-3">
                  <span className="text-lg">📘</span>
                  <div>
                    <p className="text-sm font-medium text-bardin-ink">Build Day Guide</p>
                    <p className="text-xs text-gray-400">What to expect before, during and after your build</p>
                  </div>
                </div>
                <span className="text-xs px-2 py-1 rounded font-medium bg-blue-100 text-blue-700">View</span>
              </a>
              {documents.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-4">
                  No documents available yet. Documents will appear here as your project progresses.
                </p>
              ) : (
                <div className="space-y-2">
                  {documents.map((doc, i) => {
                    // Signed but no PDF yet: render as non-clickable with a
                    // "preparing" note. The HO still sees the document is
                    // signed (status chip green) but clicking does nothing.
                    const noPdf = doc.status === "signed" && !doc.url;
                    const Wrapper: React.ElementType = doc.url ? "a" : "div";
                    const wrapperProps = doc.url
                      ? { href: doc.url, target: "_blank", rel: "noopener noreferrer" }
                      : {};
                    return (
                      <Wrapper
                        key={i}
                        {...wrapperProps}
                        className={`flex items-center justify-between border border-gray-200 rounded-lg p-3 transition ${
                          doc.url ? "hover:bg-bardin-sky/30 cursor-pointer" : "bg-gray-50"
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <span className="text-lg">
                            {doc.status === "signed" ? "✅" : doc.status === "pending" ? "⏳" : "📄"}
                          </span>
                          <div>
                            <p className="text-sm font-medium text-bardin-ink">{doc.label}</p>
                            {doc.date && <p className="text-xs text-gray-400">{fmtShortDate(doc.date)}</p>}
                            {noPdf && (
                              <p className="text-xs text-amber-700 mt-0.5">PDF being prepared — your RM will follow up.</p>
                            )}
                          </div>
                        </div>
                        <span className={`text-xs px-2 py-1 rounded font-medium ${
                          doc.status === "signed" ? "bg-green-100 text-green-700" :
                          doc.status === "pending" ? "bg-amber-100 text-amber-700" :
                          "bg-blue-100 text-blue-700"
                        }`}>
                          {doc.status === "signed" ? "Signed" : doc.status === "pending" ? "Needs Signature" : "View"}
                        </span>
                      </Wrapper>
                    );
                  })}
                </div>
              )}
            </Section>

            {/* Upload Documents */}
            <Section title="Upload Documents" icon="📤">
              <p className="text-sm text-gray-500 mb-3">
                Send us photos or documents related to your project.
              </p>
              <div
                className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition
                  ${dragActive ? "border-bardin-orange bg-bardin-orange/5" : "border-gray-300 hover:border-bardin-teal"}`}
                onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
                onDragLeave={() => setDragActive(false)}
                onDrop={(e) => { e.preventDefault(); setDragActive(false); handleFiles(e.dataTransfer.files); }}
                onClick={() => fileInputRef.current?.click()}
              >
                <img src={DUCK_IMG} alt="" className="w-10 h-10 mx-auto mb-2 opacity-60" />
                <p className="text-sm font-medium text-bardin-ink">
                  Tap to select files or drag & drop
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  Photos, PDFs, insurance documents, check images
                </p>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept="image/*,.pdf,.doc,.docx"
                  className="hidden"
                  onChange={(e) => handleFiles(e.target.files)}
                />
              </div>

              {/* Quick upload buttons */}
              <div className="mt-3 grid grid-cols-2 gap-2">
                {[
                  { icon: "📋", label: "Insurance Scope" },
                  { icon: "💳", label: "Check Photo" },
                  { icon: "📸", label: "Damage Photo" },
                  { icon: "📝", label: "Signed Document" },
                  { icon: "🚁", label: "Drone Report" },
                ].map((t) => (
                  <button
                    key={t.label}
                    onClick={() => fileInputRef.current?.click()}
                    className="flex items-center gap-2 border border-gray-200 rounded-lg px-3 py-2 text-sm text-bardin-ink hover:bg-bardin-sky/50 transition"
                  >
                    <span>{t.icon}</span>
                    <span>{t.label}</span>
                  </button>
                ))}
              </div>

              {/* Upload progress */}
              {uploads.length > 0 && (
                <div className="mt-3 space-y-2">
                  {uploads.map((f) => (
                    <div key={f.id} className="flex items-center gap-3 text-sm bg-gray-50 rounded-lg p-2">
                      <span>{f.status === "uploading" ? "⏳" : f.status === "done" ? "✅" : "❌"}</span>
                      <span className="flex-1 truncate">{f.name}</span>
                      <span className={
                        f.status === "done" ? "text-green-600 text-xs" :
                        f.status === "error" ? "text-red-500 text-xs" :
                        "text-gray-400 text-xs"
                      }>
                        {f.status === "uploading" ? "Uploading..." : f.status === "done" ? "Done" : "Failed"}
                      </span>
                    </div>
                  ))}
                </div>
              )}
              {uploadError && (
                <p className="text-xs text-red-600 mt-2">{uploadError}</p>
              )}

              <p className="text-xs text-gray-400 mt-3 text-center">
                You can also text photos directly to (470) 823-8816
              </p>
            </Section>
          </>
        )}

        {/* ═══════════════ PAYMENT TAB ═══════════════ */}
        {tab === "payment" && (
          <>
            <BankVerificationNotice items={bankVerifications} />

            {/* Payment Schedule — same Revised-A two-card layout as the
                signed install agreement PDF. Live: supplements appear in the
                After Build card automatically as the carrier approves them. */}
            <Section title="Payment Schedule" icon="📅">
              <PaymentScheduleCard deal={deal as unknown as Record<string, unknown>} />
              {/* Only before Apex has returned a number — once a request is
                  outstanding the PaymentScheduleCard carries its own pending
                  note, so this banner steps aside to avoid two notices. */}
              {deal.supplement_needed && deal.supplement_status !== "paid" && (deal.supplement_approved_amount ?? 0) === 0 && !(Number(deal.supplement_invoice_amount ?? 0) > 0) && (
                <div className="mt-3 bg-blue-50 rounded-lg p-3">
                  <p className="text-xs text-blue-700">
                    <span className="font-semibold">Supplement in progress:</span> A supplement has been submitted to your insurance company. Once approved, the additional funds will appear in the "After Build" card above and your Initial Invoice Total will update automatically.
                  </p>
                </div>
              )}
            </Section>

            {/* Payment Summary — what's actually been paid + balance */}
            <Section title="Payment Summary" icon="💳">
              <div className="space-y-2">
                {slot === "post_build" && invoicedForPostBuild > 0 && (
                  <InfoRow label="Invoice Total" value={fmtUSD(invoicedForPostBuild)} />
                )}
                {slot === "post_build" && writtenOff > 0 && (
                  <InfoRow label="Adjustment" value={`−${fmtUSD(writtenOff)}`} />
                )}
                {deal.is_retail !== true && deal.deductible != null && deal.deductible > 0 && <InfoRow label="Your Deductible" value={fmtUSD(deal.deductible)} />}
                {amountPaid > 0 && (
                  <div className="flex justify-between text-sm py-1">
                    <span className="text-gray-500">Amount Paid</span>
                    <span className="font-medium text-green-600 text-right">
                      {fmtUSD(amountPaid)}
                      {awaitingVerify > 0 && (
                        <span className="block text-xs font-semibold text-amber-700">
                          includes {fmtUSD(awaitingVerify)} waiting on your bank verification
                        </span>
                      )}
                      {processingOnly > 0 && (
                        <span className="block text-xs font-normal text-gray-400">
                          includes {fmtUSD(processingOnly)} processing
                        </span>
                      )}
                    </span>
                  </div>
                )}
                <div className="border-t pt-2 flex justify-between">
                  <span className="font-semibold text-bardin-ink">Balance Due</span>
                  <span className={`font-bold text-lg ${
                    isFullyPaid ? (awaitingVerify > 0 ? "text-amber-700" : "text-green-600")
                      : balance > 0 ? "text-bardin-orange"
                      : isFirstPaymentReceived ? "text-bardin-teal"
                      : "text-gray-500"
                  }`}>
                    {isFullyPaid
                      ? (awaitingVerify > 0 ? "Verify your bank to finish" : "Paid in Full")
                      : balance > 0
                      ? fmtUSD(balance)
                      : isFirstPaymentReceived
                      ? (deal.is_retail === true ? "Final payment due on completion" : "Pending depreciation settlement")
                      : "Pending — final invoice not yet calculated"}
                  </span>
                </div>
                {isFirstPaymentReceived && awaitingVerify > 0 && (
                  <p className="text-xs text-amber-700 pt-1">
                    {fmtUSD(awaitingVerify)} of your payment won't go through until you verify your bank account. See the note at the top of this page.
                  </p>
                )}
                {isFirstPaymentReceived && awaitingVerify === 0 && (
                  <p className="text-xs text-gray-500 pt-1">
                    {tradesDueAtCompletion > 0
                      ? deal.is_retail === true
                        ? `Your deposit is in. The other 50% and the trades billed to you (${fmtUSD(tradesDueAtCompletion)}) are due at completion.`
                        : `Your first payment is in. The depreciation portion will be released by your insurance company once we invoice them. The trades billed to you (${fmtUSD(tradesDueAtCompletion)}) are due at completion.`
                      : "Your first payment is in. The depreciation portion will be released by your insurance company once we invoice them — there's nothing for you to do."}
                  </p>
                )}
              </div>
            </Section>

            {/* Carrier-safe downloadable documents (COC) — renders only once
                the build is complete. */}
            <CocDownloadCard deal={deal as unknown as Record<string, unknown>} />

            {/* Pay Online */}
            {balance > 0 ? (
              <Section title="Pay Online" icon="🔒">
                {/* Amount selector */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-bardin-ink mb-2">Payment Amount</label>
                  <div className="flex gap-2 mb-2">
                    <button
                      onClick={() => setCustomAmount("")}
                      className={`flex-1 py-2 rounded-lg text-sm font-medium border-2 transition ${
                        !customAmount
                          ? "border-bardin-teal bg-bardin-teal/10 text-bardin-teal"
                          : "border-gray-200 text-gray-500 hover:border-gray-300"
                      }`}
                    >
                      Full Balance: {fmtUSD(balance)}
                    </button>
                    <button
                      onClick={() => setCustomAmount(String(Math.min(balance, Math.round(balance / 2))))}
                      className={`flex-1 py-2 rounded-lg text-sm font-medium border-2 transition ${
                        customAmount
                          ? "border-bardin-teal bg-bardin-teal/10 text-bardin-teal"
                          : "border-gray-200 text-gray-500 hover:border-gray-300"
                      }`}
                    >
                      Custom Amount
                    </button>
                  </div>
                  {customAmount !== "" && (
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-lg font-medium">$</span>
                      <input
                        type="number"
                        value={customAmount}
                        onChange={(e) => setCustomAmount(e.target.value)}
                        min="1"
                        max={balance}
                        step="0.01"
                        placeholder="Enter amount"
                        className="w-full border border-gray-300 rounded-lg pl-8 pr-4 py-3 text-lg font-medium focus:ring-2 focus:ring-bardin-teal focus:border-transparent"
                      />
                      {payAmount > balance && (
                        <p className="text-red-500 text-xs mt-1">Cannot exceed balance of {fmtUSD(balance)}</p>
                      )}
                    </div>
                  )}
                  {isPartialPayment && (
                    <p className="text-xs text-gray-500 mt-1">
                      Remaining balance after this payment: {fmtUSD(balance - payAmount)}
                    </p>
                  )}
                </div>

                <div className="space-y-3">
                  {/* ACH Button — No Fee */}
                  <button
                    onClick={() => handlePayNow("ach")}
                    disabled={creatingLink !== null || payAmount <= 0 || payAmount > balance}
                    className="w-full bg-green-600 text-white font-semibold rounded-lg py-3.5 hover:bg-green-700 transition disabled:opacity-50 text-lg shadow-sm"
                  >
                    {creatingLink === "ach" ? (
                      "Setting up bank transfer..."
                    ) : (
                      <>
                        <span className="block">Pay {fmtUSD(payAmount)} via Bank Transfer</span>
                        <span className="block text-xs font-normal text-green-100 mt-0.5">No processing fee</span>
                      </>
                    )}
                  </button>

                  {/* Card Button — 3% Fee */}
                  <button
                    onClick={() => handlePayNow("card")}
                    disabled={creatingLink !== null || payAmount <= 0 || payAmount > balance}
                    className="w-full bg-gray-600 text-white font-semibold rounded-lg py-3.5 hover:bg-gray-700 transition disabled:opacity-50 text-lg shadow-sm"
                  >
                    {creatingLink === "card" ? (
                      "Setting up card payment..."
                    ) : (
                      <>
                        <span className="block">Pay {fmtUSD(cardTotal)} via Credit/Debit Card</span>
                        <span className="block text-xs font-normal text-gray-300 mt-0.5">
                          {cardSurchargeApplies
                            ? `Includes ${fmtUSD(cardSurcharge)} processing fee (3%)`
                            : "No processing fee on payments $1,500 or less"}
                        </span>
                      </>
                    )}
                  </button>
                </div>

                <p className="text-xs text-gray-400 mt-3 text-center">
                  Secure payments powered by Stripe.
                </p>

                {linkError && (
                  <p className="text-red-500 text-sm mt-3 text-center">{linkError}</p>
                )}

                <div className="mt-4 border-t pt-4">
                  <h3 className="text-sm font-medium text-bardin-ink mb-1">Pay by Check</h3>
                  <p className="text-xs text-gray-500">
                    Make checks payable to <span className="font-medium">Peach Duck Contracting Company</span>.
                    Your Restore Manager can collect it, or mail to our office.
                  </p>
                </div>
              </Section>
            ) : isFullyPaid && awaitingVerify > 0 ? (
              <Section title="Payment Status" icon="🏦">
                <div className="text-center py-4">
                  <h3 className="font-bold text-amber-700 text-xl">One step left</h3>
                  <p className="text-sm text-gray-500 mt-1">
                    Your payments cover the balance, but {fmtUSD(awaitingVerify)} won't go through until you verify your bank account. See the note at the top of this page.
                  </p>
                </div>
              </Section>
            ) : isFullyPaid ? (
              <Section title="Payment Status" icon="✅">
                <div className="text-center py-4">
                  <img src={DUCK_IMG} alt="" className="w-16 h-16 mx-auto mb-3" />
                  <h3 className="font-bold text-green-600 text-xl">Paid in Full!</h3>
                  <p className="text-sm text-gray-500 mt-1">
                    Thank you for your payment.
                    {processingOnly > 0 && ` ${fmtUSD(processingOnly)} is still processing — no action needed.`}
                  </p>
                </div>
              </Section>
            ) : isFirstPaymentReceived && awaitingVerify > 0 ? (
              <Section title="Payment Status" icon="🏦">
                <div className="text-center py-4">
                  <h3 className="font-bold text-amber-700 text-xl">Payment started</h3>
                  <p className="text-sm text-gray-500 mt-1">
                    Thanks! {fmtUSD(awaitingVerify)} of your payment is waiting on you to verify your bank account. See the note at the top of this page.
                  </p>
                </div>
              </Section>
            ) : isFirstPaymentReceived ? (
              <Section title="Payment Status" icon="💸">
                <div className="text-center py-4">
                  <img src={DUCK_IMG} alt="" className="w-16 h-16 mx-auto mb-3" />
                  <h3 className="font-bold text-bardin-teal text-xl">First payment received</h3>
                  <p className="text-sm text-gray-500 mt-1">
                    Thanks! We've got your first payment of {fmtUSD(amountPaid)}. The depreciation settlement comes directly from your insurance once we invoice them — we'll keep you posted, but there's nothing else you need to do right now.
                  </p>
                </div>
              </Section>
            ) : (
              <Section title="Payment Status" icon="⏳">
                <div className="text-center py-4">
                  <h3 className="font-semibold text-bardin-ink text-lg">Final invoice in progress</h3>
                  <p className="text-sm text-gray-500 mt-1">
                    Your build is complete. We're finalizing your invoice with insurance — payment details will appear here once it's ready.
                  </p>
                </div>
              </Section>
            )}
          </>
        )}

        {/* ═══════════════ REFERRALS TAB ═══════════════ */}
        {tab === "referrals" && <ReferralsTab deal={deal} />}

        {/* ─── Footer ─── */}
        <div className="text-center text-sm text-gray-400 pb-6">
          Questions? Call or text{" "}
          <a href="tel:+14708238816" className="text-bardin-teal font-medium">
            (470) 823-8816
          </a>
        </div>
      </div>
    </HomeownerGate>
  );
}
